import React, { useState, useEffect, useCallback } from "react";
import { auditApi, userApi, tenantApi } from "../../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const AuditLogViewer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingLogs, setExportingLogs] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
  });

  // Summary stats
  const [stats, setStats] = useState({
    success_count: 0,
    failed_count: 0,
    phi_count: 0,
  });

  // Tenant filter for is_primary users
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");

  // Filter states
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    user_email: "",
    action: "",
    entity_type: "",
    contains_phi: "",
    action_status: "",
    purpose_of_use: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [dateRange, setDateRange] = useState("all");

  // Dynamic filter options
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    groupedActions: {},
    entityTypes: [],
    statuses: [],
    purposes: [],
    purposesOfUse: [],
  });

  // Fetch tenants for is_primary users
  useEffect(() => {
    if (user?.is_primary) {
      const fetchTenants = async () => {
        try {
          const response = await tenantApi.getAll();
          if (response.data) {
            setTenants(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch tenants:", error);
        }
      };
      fetchTenants();
    }
  }, [user?.is_primary]);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        start_date: filters.date_from
          ? filters.date_from.replace("T", " ")
          : "",
        end_date: filters.date_to ? filters.date_to.replace("T", " ") : "",
        user_email: filters.user_email,
        action: filters.action,
        entity_type: filters.entity_type,
        contains_phi: filters.contains_phi,
        action_status: filters.action_status,
        purpose_of_use: filters.purpose_of_use,
      };

      // Add tenant filter for is_primary users
      if (user?.is_primary && selectedTenant) {
        params.filter_tenant_id = selectedTenant;
      }

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await auditApi.getLogs(params);

      if (response.data) {
        const logs = response.data.logs || response.data;
        setLogs(logs);

        setPagination((prev) => ({
          ...prev,
          total:
            response.data.pagination?.total ||
            response.data.total ||
            logs.length ||
            0,
        }));

        if (response.data.stats) {
          setStats(response.data.stats);
        } else {
          const calculatedStats = {
            success_count: logs.filter(
              (log) => log.action_status?.toLowerCase() === "success"
            ).length,
            failed_count: logs.filter((log) =>
              ["failed", "error", "failure"].includes(
                log.action_status?.toLowerCase()
              )
            ).length,
            phi_count: logs.filter((log) => log.contains_phi === true).length,
          };
          setStats(calculatedStats);
        }
      }
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, filters, selectedTenant, user?.is_primary]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await userApi.getAll();
      if (response.data) {
        setAvailableUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await auditApi.getFilterOptions();
      if (response.data) {
        const mappedOptions = {
          actions: response.data.actions || [],
          groupedActions: response.data.groupedActions || {},
          entityTypes: response.data.entityTypes || [],
          statuses: response.data.statuses || [],
          purposes: response.data.purposesOfUse || [],
          purposesOfUse: response.data.purposesOfUse || [],
        };
        setFilterOptions(mappedOptions);
      }
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
      toast.error("Failed to load filter options");
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchFilterOptions();
  }, [fetchUsers, fetchFilterOptions]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const now = new Date();
    let fromDate = "";
    let toDate = new Date().toISOString();

    switch (range) {
      case "today":
        fromDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = new Date(yesterday.setHours(0, 0, 0, 0)).toISOString();
        toDate = new Date(yesterday.setHours(23, 59, 59, 999)).toISOString();
        break;
      case "last7days":
        fromDate = new Date(now.setDate(now.getDate() - 7)).toISOString();
        break;
      case "last30days":
        fromDate = new Date(now.setDate(now.getDate() - 30)).toISOString();
        break;
      case "thisMonth":
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        break;
      case "lastMonth":
        fromDate = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1
        ).toISOString();
        toDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          0,
          23,
          59,
          59,
          999
        ).toISOString();
        break;
      case "custom":
        return;
      default:
        fromDate = "";
        toDate = "";
    }

    setFilters((prev) => ({
      ...prev,
      date_from: fromDate ? fromDate.slice(0, 16) : "",
      date_to: toDate ? toDate.slice(0, 16) : "",
    }));
  };

  const applyFilters = () => {
    setPagination({ ...pagination, page: 1 });
    fetchLogs();
  };

  const handleExport = async () => {
    setExportingLogs(true);
    try {
      const params = {
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== "")
        ),
        format: "csv",
      };

      const response = await auditApi.exportLogs(params);
      const blob = response.data || response;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Audit logs exported successfully");
    } catch (error) {
      console.error("Failed to export logs:", error);
      toast.error("Failed to export audit logs");
    } finally {
      setExportingLogs(false);
    }
  };

  const toggleRowExpansion = (auditId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(auditId)) {
      newExpanded.delete(auditId);
    } else {
      newExpanded.add(auditId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "â€”";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // âœ… FIXED: Better date formatting for mobile
  const formatDateMobile = (dateString) => {
    if (!dateString) return "â€”";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionBadgeClass = (action) => {
    const actionLower = action?.toLowerCase() || "";

    if (actionLower.includes("login") || actionLower.includes("logout"))
      return "badge bg-primary";
    if (actionLower.includes("export") || actionLower.includes("phi"))
      return "badge bg-warning text-dark";
    if (actionLower.includes("delete")) return "badge bg-danger";
    if (actionLower.includes("update") || actionLower.includes("edit"))
      return "badge bg-info";
    if (actionLower.includes("create") || actionLower.includes("add"))
      return "badge bg-success";
    if (actionLower.includes("view") || actionLower.includes("access"))
      return "badge bg-secondary";
    if (actionLower.includes("unauthorized")) return "badge bg-danger";

    return "badge bg-secondary";
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "";

    if (statusLower === "success") {
      return <span className="badge bg-success">Success</span>;
    } else if (statusLower === "failed" || statusLower === "error") {
      return <span className="badge bg-danger">Failed</span>;
    } else if (statusLower === "unauthorized") {
      return <span className="badge bg-warning text-dark">Unauthorized</span>;
    }

    return <span className="badge bg-secondary">{status}</span>;
  };

  const resetFilters = () => {
    setFilters({
      date_from: "",
      date_to: "",
      user_email: "",
      action: "",
      entity_type: "",
      contains_phi: "",
      action_status: "",
      purpose_of_use: "",
    });
    setDateRange("all");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    // âœ… FIXED: Remove container-fluid and use simple div with proper overflow handling
    <div
      className="w-100"
      style={{
        padding: "1rem",
        minHeight: "100vh",
        maxWidth: "100vw",
        overflowX: "auto",
      }}
    >
      {/* âœ… FIXED: Better responsive header */}
      <div className="mb-3 mb-lg-4">
        <div className="d-flex justify-content-between align-items-start mb-3 mb-lg-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="bi bi-shield-lock fs-3 fs-lg-2 me-2 me-lg-3 d-none d-sm-block"></i>
            <div>
              <h1 className="h3 h-lg-2 mb-1">Audit Log Viewer</h1>
              <p className="text-muted mb-0 d-none d-lg-block">
                Monitor and track all system activities
              </p>
            </div>
          </div>
          <div className="d-flex gap-1 gap-lg-2 flex-wrap">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            >
              <i
                className={`bi bi-arrow-clockwise me-1 me-lg-2 ${
                  loading ? "spinner-border spinner-border-sm" : ""
                }`}
              ></i>
              <span className="d-none d-sm-inline">Refresh</span>
            </button>
            <button
              onClick={handleExport}
              disabled={exportingLogs}
              className="btn btn-primary btn-sm d-flex align-items-center"
            >
              <i className="bi bi-download me-1 me-lg-2"></i>
              <span className="d-none d-sm-inline">
                {exportingLogs ? "Exporting..." : "Export CSV"}
              </span>
              <span className="d-sm-none">Export</span>
            </button>
          </div>
        </div>

        {/* âœ… FIXED: Consumer Information Privacy Notice */}
        <div className="alert alert-info d-flex align-items-start mb-3">
          <i className="bi bi-info-circle-fill me-2 mt-1 flex-shrink-0"></i>
          <div>
            <strong>Consumer Information Privacy Notice:</strong>
            <span className="d-none d-lg-inline">
              {" "}
              This audit log contains sensitive consumer information for
              vocational rehabilitation services. All access is logged and
              monitored. Only view records necessary for your authorized CRP
              service purposes.
            </span>
            <span className="d-lg-none">
              {" "}
              Consumer access is logged and monitored.
            </span>
          </div>
        </div>

        {/* Tenant Filter for is_primary users */}
        {user?.is_primary && (
          <div className="alert alert-info mb-3 d-flex flex-column flex-lg-row align-items-lg-center gap-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-building me-2"></i>
              <strong className="me-2">Tenant Filter:</strong>
            </div>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: "300px" }}
              value={selectedTenant}
              onChange={(e) => {
                setSelectedTenant(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">All Tenants</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.tenant_name}
                </option>
              ))}
            </select>
            {!selectedTenant && (
              <small className="text-muted ms-lg-2">
                Showing audit logs from all tenants
              </small>
            )}
          </div>
        )}

        {/* Stats grid */}
        {!loading && (
          <div className="row mb-3 g-2">
            <div className="col-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body p-2 p-lg-3">
                  <h5 className="card-title mb-1 fs-6 fs-lg-5">
                    {pagination.total}
                  </h5>
                  <p className="card-text text-muted small mb-0">Total</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body p-2 p-lg-3">
                  <h5 className="card-title text-success mb-1 fs-6 fs-lg-5">
                    {stats.success_count}
                  </h5>
                  <p className="card-text text-muted small mb-0">Success</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body p-2 p-lg-3">
                  <h5 className="card-title text-danger mb-1 fs-6 fs-lg-5">
                    {stats.failed_count}
                  </h5>
                  <p className="card-text text-muted small mb-0">Failed</p>
                </div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="card text-center h-100">
                <div className="card-body p-2 p-lg-3">
                  <h5 className="card-title text-warning mb-1 fs-6 fs-lg-5">
                    {stats.phi_count}
                  </h5>
                  <p className="card-text text-muted small mb-0">
                    Consumer Info
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* âœ… FIXED: Better responsive filters */}
        <div className="card mb-3 mb-lg-4">
          <div className="card-header d-flex justify-content-between align-items-center py-2 py-lg-3">
            <h5 className="mb-0 d-flex align-items-center fs-6 fs-lg-5">
              <i className="bi bi-funnel me-2"></i>
              Filters
            </h5>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-link btn-sm text-decoration-none p-0"
            >
              <i className={`bi bi-chevron-${showFilters ? "up" : "down"}`}></i>
            </button>
          </div>

          {showFilters && (
            <div className="card-body p-2 p-lg-3">
              {/* Quick Date Range Selector */}
              <div className="mb-3">
                <label className="form-label fw-bold small mb-2">
                  Quick Date Range
                </label>
                <div className="d-flex flex-wrap gap-1">
                  {[
                    "all",
                    "today",
                    "yesterday",
                    "last7days",
                    "last30days",
                    "thisMonth",
                    "lastMonth",
                    "custom",
                  ].map((range) => (
                    <button
                      key={range}
                      type="button"
                      className={`btn btn-sm ${
                        dateRange === range
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => handleDateRangeChange(range)}
                    >
                      <span className="d-none d-xl-inline">
                        {range === "all"
                          ? "All Time"
                          : range === "today"
                          ? "Today"
                          : range === "yesterday"
                          ? "Yesterday"
                          : range === "last7days"
                          ? "Last 7 Days"
                          : range === "last30days"
                          ? "Last 30 Days"
                          : range === "thisMonth"
                          ? "This Month"
                          : range === "lastMonth"
                          ? "Last Month"
                          : "Custom"}
                      </span>
                      <span className="d-xl-none">
                        {range === "all"
                          ? "All"
                          : range === "today"
                          ? "Today"
                          : range === "yesterday"
                          ? "Yesterday"
                          : range === "last7days"
                          ? "7d"
                          : range === "last30days"
                          ? "30d"
                          : range === "thisMonth"
                          ? "This Mo"
                          : range === "lastMonth"
                          ? "Last Mo"
                          : "Custom"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Range */}
              {dateRange === "custom" && (
                <div className="row g-2 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label small">Date From</label>
                    <input
                      type="datetime-local"
                      value={filters.date_from}
                      onChange={(e) =>
                        setFilters({ ...filters, date_from: e.target.value })
                      }
                      className="form-control form-control-sm"
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small">Date To</label>
                    <input
                      type="datetime-local"
                      value={filters.date_to}
                      onChange={(e) =>
                        setFilters({ ...filters, date_to: e.target.value })
                      }
                      className="form-control form-control-sm"
                    />
                  </div>
                </div>
              )}

              {/* âœ… FIXED: Better responsive filter grid */}
              <div className="row g-2 mb-3">
                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">User</label>
                  <select
                    value={filters.user_email}
                    onChange={(e) =>
                      setFilters({ ...filters, user_email: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Users</option>
                    {availableUsers.map((user) => (
                      <option key={user.user_id} value={user.email}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">Action Type</label>
                  <select
                    value={filters.action}
                    onChange={(e) =>
                      setFilters({ ...filters, action: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Actions</option>
                    {Object.entries(filterOptions.groupedActions || {}).map(
                      ([categoryKey, group]) => (
                        <optgroup key={categoryKey} label={group.label}>
                          {(group.actions || []).map((action, index) => (
                            <option
                              key={`${categoryKey}-${action}-${index}`}
                              value={action}
                            >
                              {action
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </option>
                          ))}
                        </optgroup>
                      )
                    )}
                  </select>
                </div>

                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">Entity Type</label>
                  <select
                    value={filters.entity_type}
                    onChange={(e) =>
                      setFilters({ ...filters, entity_type: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Types</option>
                    {(filterOptions.entityTypes || []).map((type) => (
                      <option key={type} value={type}>
                        {type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">Status</label>
                  <select
                    value={filters.action_status}
                    onChange={(e) =>
                      setFilters({ ...filters, action_status: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Statuses</option>
                    {(filterOptions.statuses || []).map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">
                    Contains Protected Info
                  </label>
                  <select
                    value={filters.contains_phi}
                    onChange={(e) =>
                      setFilters({ ...filters, contains_phi: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Records</option>
                    <option value="true">Contains Protected Info</option>
                    <option value="false">No Protected Info</option>
                  </select>
                </div>

                <div className="col-12 col-lg-6 col-xl-4">
                  <label className="form-label small">Purpose</label>
                  <select
                    value={filters.purpose_of_use}
                    onChange={(e) =>
                      setFilters({ ...filters, purpose_of_use: e.target.value })
                    }
                    className="form-select form-select-sm"
                  >
                    <option value="">All Purposes</option>
                    {(filterOptions.purposesOfUse || []).length > 0 ? (
                      (filterOptions.purposesOfUse || []).map((purpose) => (
                        <option key={purpose} value={purpose}>
                          {purpose
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="vocational_services">
                          Vocational Services
                        </option>
                        <option value="employment_support">
                          Employment Support
                        </option>
                        <option value="life_skills_training">
                          Life Skills Training
                        </option>
                        <option value="case_management">Case Management</option>
                        <option value="assessment_evaluation">
                          Assessment & Evaluation
                        </option>
                        <option value="reporting">Reporting</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="d-flex justify-content-end gap-2 flex-wrap">
                <button
                  onClick={resetFilters}
                  className="btn btn-outline-secondary btn-sm"
                >
                  <i className="bi bi-x-circle me-1 me-lg-2"></i>
                  <span className="d-none d-sm-inline">Reset Filters</span>
                  <span className="d-sm-none">Reset</span>
                </button>
                <button
                  onClick={applyFilters}
                  className="btn btn-primary btn-sm"
                >
                  <i className="bi bi-funnel me-1 me-lg-2"></i>
                  <span className="d-none d-sm-inline">Apply Filters</span>
                  <span className="d-sm-none">Apply</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* âœ… FIXED: Better responsive audit log display */}
      <div className="card overflow-hidden">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center p-4 p-lg-5"
            style={{ minHeight: "300px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-4 py-lg-5">
            <i className="bi bi-file-earmark-text fs-1 text-muted mb-3 d-block"></i>
            <h5>No audit logs found</h5>
            <p className="text-muted">
              Try adjusting your filters or date range.
            </p>
          </div>
        ) : (
          <>
            {/* âœ… FIXED: Desktop Table View - Always use horizontal scroll on smaller screens */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table
                  className="table table-hover mb-0"
                  style={{ minWidth: "1000px" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th scope="col" style={{ width: "140px" }}>
                        Timestamp
                      </th>
                      <th scope="col" style={{ width: "180px" }}>
                        User
                      </th>
                      <th scope="col" style={{ width: "120px" }}>
                        Action
                      </th>
                      <th scope="col" style={{ width: "120px" }}>
                        Entity
                      </th>
                      <th scope="col" style={{ width: "80px" }}>
                        Status
                      </th>
                      <th scope="col" style={{ width: "80px" }}>
                        Protected
                      </th>
                      <th scope="col" style={{ width: "80px" }}>
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <React.Fragment key={log.audit_id}>
                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-clock text-muted me-2"></i>
                              <small>{formatDate(log.created_at)}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div
                                className="fw-medium text-truncate"
                                style={{ maxWidth: "160px" }}
                              >
                                {log.user_name || "Unknown User"}
                              </div>
                              <small
                                className="text-muted text-truncate d-block"
                                style={{ maxWidth: "160px" }}
                              >
                                {log.user_email}
                              </small>
                              {log.user_role && (
                                <div className="mt-1">
                                  <span className="badge bg-secondary">
                                    {log.user_role}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={getActionBadgeClass(log.action)}>
                              {log.action}
                            </span>
                          </td>
                          <td>
                            <div>
                              <div className="fw-medium">{log.entity_type}</div>
                              {log.entity_identifier && (
                                <small
                                  className="text-muted d-block text-truncate"
                                  style={{ maxWidth: "100px" }}
                                >
                                  {log.entity_identifier}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{getStatusBadge(log.action_status)}</td>
                          <td>
                            {log.contains_phi && (
                              <div className="text-danger">
                                <i className="bi bi-lock-fill me-1"></i>
                                <small className="fw-medium">Protected</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={() => toggleRowExpansion(log.audit_id)}
                              className="btn btn-sm btn-outline-primary"
                            >
                              {expandedRows.has(log.audit_id) ? (
                                <i className="bi bi-chevron-up"></i>
                              ) : (
                                <i className="bi bi-chevron-down"></i>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Details Row */}
                        {expandedRows.has(log.audit_id) && (
                          <tr>
                            <td colSpan="7" className="bg-light">
                              <div className="p-3">
                                <div className="row">
                                  <div className="col-lg-6 mb-3 mb-lg-0">
                                    <h6 className="fw-bold mb-3">
                                      Audit Details
                                    </h6>
                                    <dl className="row mb-0">
                                      <dt className="col-sm-4 text-truncate">
                                        Audit ID:
                                      </dt>
                                      <dd className="col-sm-8">
                                        <code className="small text-break">
                                          {log.audit_id}
                                        </code>
                                      </dd>

                                      <dt className="col-sm-4 text-truncate">
                                        IP Address:
                                      </dt>
                                      <dd className="col-sm-8">
                                        {log.ip_address || "N/A"}
                                      </dd>

                                      {log.purpose_of_use && (
                                        <>
                                          <dt className="col-sm-4 text-truncate">
                                            Purpose:
                                          </dt>
                                          <dd className="col-sm-8">
                                            {log.purpose_of_use}
                                          </dd>
                                        </>
                                      )}

                                      {log.data_sensitivity_level && (
                                        <>
                                          <dt className="col-sm-4 text-truncate">
                                            Sensitivity:
                                          </dt>
                                          <dd className="col-sm-8">
                                            <span className="badge bg-warning text-dark">
                                              {log.data_sensitivity_level}
                                            </span>
                                          </dd>
                                        </>
                                      )}

                                      {log.session_id && (
                                        <>
                                          <dt className="col-sm-4 text-truncate">
                                            Session ID:
                                          </dt>
                                          <dd className="col-sm-8">
                                            <code className="small text-break">
                                              {log.session_id}
                                            </code>
                                          </dd>
                                        </>
                                      )}
                                    </dl>
                                  </div>

                                  {log.fields_accessed &&
                                    Array.isArray(log.fields_accessed) &&
                                    log.fields_accessed.length > 0 && (
                                      <div className="col-lg-6">
                                        <h6 className="fw-bold mb-3">
                                          Fields Accessed
                                        </h6>
                                        <div className="d-flex flex-wrap gap-2">
                                          {log.fields_accessed.map(
                                            (field, idx) => (
                                              <span
                                                key={idx}
                                                className="badge bg-secondary"
                                              >
                                                {field}
                                              </span>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  {log.record_count && (
                                    <div className="col-lg-6">
                                      <h6 className="fw-bold mb-3">
                                        Export Details
                                      </h6>
                                      <p className="mb-0">
                                        Records exported:{" "}
                                        <strong>{log.record_count}</strong>
                                      </p>
                                    </div>
                                  )}

                                  {log.action_reason && (
                                    <div className="col-12 mt-3">
                                      <h6 className="fw-bold mb-2">
                                        Action Reason
                                      </h6>
                                      <p className="mb-0 text-break">
                                        {log.action_reason}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* âœ… FIXED: Mobile Card View for small screens */}
            <div className="d-md-none">
              {logs.map((log) => (
                <div key={log.audit_id} className="border-bottom p-3">
                  {/* Header with timestamp and status */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <small className="text-muted">
                        <i className="bi bi-clock me-1"></i>
                        {formatDateMobile(log.created_at)}
                      </small>
                    </div>
                    {getStatusBadge(log.action_status)}
                  </div>

                  {/* User info */}
                  <h6 className="mb-1 text-truncate">
                    {log.user_name || "Unknown User"}
                  </h6>
                  <p className="small text-muted mb-2 text-truncate">
                    {log.user_email}
                    {log.user_role && (
                      <span className="badge bg-secondary ms-2">
                        {log.user_role}
                      </span>
                    )}
                  </p>

                  {/* Action and Entity */}
                  <div className="mb-2">
                    <span className={getActionBadgeClass(log.action)}>
                      {log.action}
                    </span>
                    {log.contains_phi && (
                      <span className="badge bg-danger ms-2">
                        <i className="bi bi-lock-fill me-1"></i>Protected
                      </span>
                    )}
                  </div>

                  <p className="mb-2">
                    <strong>Entity:</strong> {log.entity_type}
                    {log.entity_identifier && (
                      <span className="text-muted d-block small text-truncate">
                        {log.entity_identifier}
                      </span>
                    )}
                  </p>

                  {/* Expandable details */}
                  <button
                    onClick={() => toggleRowExpansion(log.audit_id)}
                    className="btn btn-sm btn-outline-primary w-100 mt-2"
                  >
                    {expandedRows.has(log.audit_id) ? (
                      <>
                        <i className="bi bi-chevron-up me-2"></i>
                        Hide Details
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-2"></i>
                        Show Details
                      </>
                    )}
                  </button>

                  {/* Expanded content for mobile */}
                  {expandedRows.has(log.audit_id) && (
                    <div className="mt-3 pt-3 border-top">
                      <dl className="row mb-0 small">
                        <dt className="col-5">Audit ID:</dt>
                        <dd className="col-7 text-truncate">
                          <code className="small">{log.audit_id}</code>
                        </dd>

                        {log.ip_address && (
                          <>
                            <dt className="col-5">IP Address:</dt>
                            <dd className="col-7">{log.ip_address}</dd>
                          </>
                        )}

                        {log.purpose_of_use && (
                          <>
                            <dt className="col-5">Purpose:</dt>
                            <dd className="col-7">{log.purpose_of_use}</dd>
                          </>
                        )}

                        {log.data_sensitivity_level && (
                          <>
                            <dt className="col-5">Sensitivity:</dt>
                            <dd className="col-7">
                              <span className="badge bg-warning text-dark">
                                {log.data_sensitivity_level}
                              </span>
                            </dd>
                          </>
                        )}
                      </dl>

                      {log.fields_accessed &&
                        Array.isArray(log.fields_accessed) &&
                        log.fields_accessed.length > 0 && (
                          <div className="mt-2">
                            <strong className="small">Fields Accessed:</strong>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {log.fields_accessed.map((field, idx) => (
                                <span
                                  key={idx}
                                  className="badge bg-secondary small"
                                >
                                  {field}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                      {log.action_reason && (
                        <div className="mt-2">
                          <strong className="small">Action Reason:</strong>
                          <p className="mb-0 small">{log.action_reason}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* âœ… FIXED: Better responsive pagination */}
        {!loading && logs.length > 0 && (
          <div className="card-footer py-2 py-lg-3">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center gap-2 gap-lg-3">
              <div className="text-center text-lg-start">
                <small className="text-muted">
                  Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.per_page,
                    pagination.total
                  )}{" "}
                  of {pagination.total} results
                </small>
              </div>
              <div className="d-flex flex-column flex-lg-row align-items-center gap-2 gap-lg-3">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 small text-nowrap">
                    Per page:
                  </label>
                  <select
                    value={pagination.per_page}
                    onChange={(e) =>
                      setPagination({
                        ...pagination,
                        per_page: parseInt(e.target.value),
                        page: 1,
                      })
                    }
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        pagination.page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setPagination({
                            ...pagination,
                            page: Math.max(1, pagination.page - 1),
                          })
                        }
                        disabled={pagination.page === 1}
                      >
                        <span className="d-none d-lg-inline">Previous</span>
                        <span className="d-lg-none">Prev</span>
                      </button>
                    </li>
                    <li className="page-item active">
                      <span className="page-link text-nowrap">
                        <span className="d-none d-lg-inline">Page </span>
                        {pagination.page}
                        <span className="d-none d-lg-inline"> of </span>
                        <span className="d-lg-none">/</span>
                        {Math.ceil(pagination.total / pagination.per_page) || 1}
                      </span>
                    </li>
                    <li
                      className={`page-item ${
                        pagination.page * pagination.per_page >=
                        pagination.total
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setPagination({
                            ...pagination,
                            page: pagination.page + 1,
                          })
                        }
                        disabled={
                          pagination.page * pagination.per_page >=
                          pagination.total
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;